// Shared helper for human-readable tournament round names.
// Used by admin dashboard, referee portal, and public spectator page.

const KO_FORMATS = new Set(['knockout', 'double_elimination']);
const SIMPLE_ROUND_FORMATS = new Set([
  'round_robin',
  'americano',
  'mexicano',
  'mixed_americano',
  'king_of_court',
]);

const nextPow2 = (n) => {
  let b = 1;
  while (b < n) b *= 2;
  return Math.max(b, 2);
};

// Human-readable name for a KO bracket round given how many teams play in it.
//   2 → Final, 4 → Semi Finals, 8 → Quarter Finals, ≥16 → Round of N
const koNameForTeams = (teamsInRound) => {
  if (teamsInRound <= 2) return 'Final';
  if (teamsInRound === 4) return 'Semi Finals';
  if (teamsInRound === 8) return 'Quarter Finals';
  return `Round of ${teamsInRound}`;
};

// teamsInRound = bracketSize / 2^(roundWithinStage - 1)
const teamsInKoRound = (bracketSize, roundWithinStage) =>
  Math.max(2, Math.floor(bracketSize / Math.pow(2, roundWithinStage - 1)));

// Best-effort team count for a tournament. Uses tnm.team_count if present,
// otherwise counts active teams from the teams array.
const inferTeamCount = (tournament) => {
  if (typeof tournament?.team_count === 'number') return tournament.team_count;
  if (Array.isArray(tournament?.teams)) {
    return tournament.teams.filter((t) => (t.status || 'active') === 'active').length;
  }
  return 0;
};

// For group_knockout: number of qualifiers entering the KO stage.
// Prefers the actual resolved qualifier set / flexible qualification config
// (Phase 2), falling back to the legacy top-N-per-group estimate.
const inferGkQualifiers = (tournament) => {
  // 1. Actual persisted qualifier set is authoritative.
  if (Array.isArray(tournament?.qualifiers) && tournament.qualifiers.length >= 2) {
    return tournament.qualifiers.length;
  }
  const numGroups = (tournament?.groups && typeof tournament.groups === 'object')
    ? Object.keys(tournament.groups).length
    : Math.max(1, Math.ceil(inferTeamCount(tournament) / (tournament?.group_size || 4)));
  // 2. Flexible qualification config (direct per group + wildcards).
  const q = tournament?.qualification;
  if (q && typeof q === 'object') {
    const direct = q.direct_per_group || 1;
    const wildcards = q.wildcards || 0;
    return numGroups * direct + wildcards;
  }
  // 3. Legacy top-N-per-group.
  const advance = tournament?.teams_advancing_per_group || 1;
  return numGroups * advance;
};

// Compute round name for a double-elimination match. We need both round AND
// bracket. LB last-round → "Losers — Final"; LB 2nd-last → "Losers — Semi Finals".
const doubleElimRoundName = (tournament, roundNumber, bracket) => {
  if (bracket === 'grand_final') return 'Grand Final';
  if (bracket === 'reset') return 'Grand Final — Reset';
  const teamCount = inferTeamCount(tournament);
  const bracketSize = nextPow2(teamCount);
  const wbRounds = Math.max(1, Math.round(Math.log2(bracketSize)));
  if (bracket === 'winners') {
    const teams = teamsInKoRound(bracketSize, roundNumber);
    return `Winners — ${koNameForTeams(teams)}`;
  }
  if (bracket === 'losers') {
    // LB has 2 * (wbRounds - 1) rounds in a standard double-elim bracket.
    const lbRounds = Math.max(1, 2 * (wbRounds - 1));
    if (roundNumber === lbRounds) return 'Losers — Final';
    if (roundNumber === lbRounds - 1) return 'Losers — Semi Finals';
    return `Losers — Round ${roundNumber}`;
  }
  return `Round ${roundNumber}`;
};

/**
 * Returns a human-readable round name.
 * @param {object} tournament - Full tournament object (must include format; for KO formats also team_count / teams).
 * @param {number} roundNumber - The round number from the match document (1-indexed).
 * @param {object|string} [matchOrBracket] - Optional. Pass the match document (preferred — uses match.bracket / match.group) or just a bracket string ('main' / 'winners' / 'losers' / 'grand_final' / 'reset').
 * @returns {string}
 */
export const getRoundName = (tournament, roundNumber, matchOrBracket) => {
  if (!tournament || typeof roundNumber !== 'number' || roundNumber < 1) {
    return `Round ${roundNumber || '?'}`;
  }
  const fmt = tournament.format;

  // Detect bracket / group from the match object if provided
  let bracket;
  let isGroupStageMatch = false;
  if (matchOrBracket && typeof matchOrBracket === 'object') {
    bracket = matchOrBracket.bracket;
    // Group-stage discriminator for group_knockout
    isGroupStageMatch = !!matchOrBracket.group;
  } else if (typeof matchOrBracket === 'string') {
    bracket = matchOrBracket;
  }

  if (SIMPLE_ROUND_FORMATS.has(fmt)) {
    return `Round ${roundNumber}`;
  }

  if (fmt === 'knockout') {
    const teamCount = inferTeamCount(tournament);
    const bracketSize = nextPow2(teamCount);
    const teams = teamsInKoRound(bracketSize, roundNumber);
    return koNameForTeams(teams);
  }

  if (fmt === 'double_elimination') {
    return doubleElimRoundName(tournament, roundNumber, bracket || 'winners');
  }

  if (fmt === 'group_knockout') {
    const gpRounds = tournament.group_phase_rounds || tournament.total_group_rounds || 0;
    // Group stage detection: prefer explicit match.group, else use round number vs gpRounds
    if (isGroupStageMatch || (gpRounds > 0 && roundNumber <= gpRounds)) {
      // Round within group stage = roundNumber (group rounds start at 1)
      return `Group Stage — Round ${roundNumber}`;
    }
    // Knockout stage. Prefer deriving the bracket size from the ACTUAL number of
    // KO rounds (total_rounds − group_phase_rounds) — this is exact regardless of
    // byes and of how qualifiers were configured. Fall back to inferring qualifiers.
    const koRound = gpRounds > 0 ? roundNumber - gpRounds : roundNumber;
    const koRounds = (tournament.total_rounds && gpRounds)
      ? tournament.total_rounds - gpRounds : 0;
    const bracketSize = koRounds >= 1
      ? Math.pow(2, koRounds)
      : nextPow2(inferGkQualifiers(tournament));
    const teams = teamsInKoRound(bracketSize, Math.max(1, koRound));
    return koNameForTeams(teams);
  }

  return `Round ${roundNumber}`;
};

// Whether a match is in a knockout stage. Used by tiebreaker gating
// (RefereePortal and backend). For group_knockout, group-stage matches
// carry a `group` field (engine round_robin sets bracket='main' for ALL
// matches inside a group so we cannot rely on bracket alone).
export const isKnockoutStageMatch = (tournament, match) => {
  const fmt = tournament?.format;
  if (fmt === 'knockout' || fmt === 'double_elimination') return true;
  if (fmt === 'group_knockout') return !match?.group;
  return false;
};
